import { useState } from 'react';
import TransportSection from './TransportSection';
import ShowNotesSection from './ShowNotesSection';
import { useTranslation } from 'react-i18next';
import CardStack, { type StackCard } from '../components/CardStack';
import type { GuestDTO } from '../api';

/**
 * One accommodation (hotel) card. The address + lat/lng fields feed the
 * automatic distance calculator (POST /distances/calculate). The online search
 * that fills them lives at the top of the Accommodation section.
 *
 * Roadcase (P4b): one managed `.rc-card.col` per hotel. The four control
 * clusters are `.rc-card-fields` (wrapping) instead of the old fixed-width
 * flex rows, so the SAME markup fits 390 with no horizontal scrollbar.
 */
function AccommodationRow({ a, onUpdate, onDelete, canWrite = true }: { a: any; onUpdate: (patch: any) => void; onDelete: () => void; canWrite?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="rc-card col" data-acc-row>
      <div className="rc-card-fields">
        <input className="form-input" style={{ flex: '2 1 200px', minWidth: 0 }} placeholder={t('logisticsTab.hotelName')}
          value={a.hotel_name || ''} disabled={!canWrite} onChange={(e) => onUpdate({ hotel_name: e.target.value })} />
        <input className="form-input" style={{ flex: '1 1 150px', minWidth: 0 }} placeholder={t('logisticsTab.roomType')}
          value={a.room_type || ''} disabled={!canWrite} onChange={(e) => onUpdate({ room_type: e.target.value })} />
        <button className="btn btn-small btn-danger" style={{ flex: '0 0 auto', marginLeft: 'auto' }} onClick={onDelete} disabled={!canWrite}>✕</button>
      </div>
      <div className="rc-card-fields">
        <input className="form-input" style={{ flex: '3 1 220px', minWidth: 0 }} placeholder={t('logisticsTab.addressStreet')}
          value={a.address || ''} disabled={!canWrite} onChange={(e) => onUpdate({ address: e.target.value })} />
        <input className="form-input" style={{ flex: '0 1 90px', minWidth: 0 }} placeholder={t('logisticsTab.zip')}
          value={a.zip || ''} disabled={!canWrite} onChange={(e) => onUpdate({ zip: e.target.value })} />
        <input className="form-input" style={{ flex: '1 1 150px', minWidth: 0 }} placeholder={t('logisticsTab.city')}
          value={a.city || ''} disabled={!canWrite} onChange={(e) => onUpdate({ city: e.target.value })} />
        <input className="form-input" style={{ flex: '1 1 150px', minWidth: 0 }} placeholder={t('logisticsTab.country')}
          value={a.country || ''} disabled={!canWrite} onChange={(e) => onUpdate({ country: e.target.value })} />
      </div>
      <div className="rc-card-fields">
        <input className="form-input" style={{ flex: '1 1 130px', minWidth: 0 }} placeholder={t('logisticsTab.checkIn')}
          value={a.check_in || ''} disabled={!canWrite} onChange={(e) => onUpdate({ check_in: e.target.value })} />
        <input className="form-input" style={{ flex: '1 1 130px', minWidth: 0 }} placeholder={t('logisticsTab.checkOut')}
          value={a.check_out || ''} disabled={!canWrite} onChange={(e) => onUpdate({ check_out: e.target.value })} />
        <input className="form-input" style={{ flex: '2 1 180px', minWidth: 0 }} placeholder={t('logisticsTab.confirmation')}
          value={a.confirmation_number || ''} disabled={!canWrite} onChange={(e) => onUpdate({ confirmation_number: e.target.value })} />
      </div>
      <div className="rc-card-fields">
        <input className="form-input" style={{ flex: '1 1 160px', minWidth: 0 }} placeholder={t('logisticsTab.phone')}
          value={a.phone || ''} disabled={!canWrite} onChange={(e) => onUpdate({ phone: e.target.value })} />
        <input className="form-input" style={{ flex: '1 1 160px', minWidth: 0 }} placeholder={t('logisticsTab.email')}
          value={a.email || ''} disabled={!canWrite} onChange={(e) => onUpdate({ email: e.target.value })} />
        <input className="form-input" style={{ flex: '1 1 160px', minWidth: 0 }} placeholder={t('logisticsTab.website')}
          value={a.website || ''} disabled={!canWrite} onChange={(e) => onUpdate({ website: e.target.value })} />
        <input className="form-input" style={{ flex: '1 1 160px', minWidth: 0 }} placeholder={t('logisticsTab.notes')}
          value={a.notes || ''} disabled={!canWrite} onChange={(e) => onUpdate({ notes: e.target.value })} />
      </div>
    </div>
  );
}

/**
 * LogisticsTab — the "Logistics" tab of the Show detail page: guestlist,
 * transport/flights, and accommodation + rooming list.
 *
 * Extracted from ShowDetailPage (2026-08-14, Vague 3 pass 6). Props-driven.
 *
 * Roadcase (P4b): chrome harmonised to the `.rc-*` language — `.rc-card-head`
 * + mono uppercase `.rc-header-title` (+ `.rc-header-sub`) per card, managed
 * lists as `.rc-list` / `.rc-card`, control clusters as `.rc-card-fields`,
 * sub-headings as `.rc-section-title`. ZERO logic touched: every prop,
 * handler, guard (`canWrite` / `canWriteGuests`) and the mounted
 * `TransportSection` are byte-for-byte the P4a state.
 */
export interface LogisticsTabProps {
  guests: GuestDTO[];
  newGuest: string;
  setNewGuest: (v: string) => void;
  addGuest: () => void;
  updateGuest: (g: GuestDTO, patch: Partial<GuestDTO>) => void;
  removeGuest: (g: GuestDTO) => void;
  realId: string | null;
  members: any[];
  flightNotes: string;
  onFlightNotesChange: (v: string) => void;
  accommodations: any[];
  accSearch: string;
  searchAccommodation: (q: string) => void;
  accResults: any[];
  setAccSearch: (v: string) => void;
  setAccResults: (v: any[]) => void;
  addAccommodation: () => void;
  addAccommodationFromResult: (r: any) => void;
  updateAccommodation: (a: any, patch: any) => void;
  deleteAccommodation: (id: number) => void;
  roomingFor: (memberId?: string | number) => any;
  upsertRooming: (memberId: string | number, patch: any) => void;
  clearRooming: (memberId: string | number) => void;
  /** The show's band — feeds TransportSection's capability-aware gate (lot A2). */
  bandId?: string | null;
  /** Lot A2: freeze the inputs whose write path is gated upstream (dead typing). */
  canWrite?: boolean;
  /**
   * Freeze the GUESTLIST only. Defaults to `canWrite`, but the server exempts
   * `/api/shows/:id/guests` (`canWriteGuests` = band editor+, even for a
   * `read-only` account), so ShowDetailPage passes the wider
   * `writeGate.canWriteEditor` here — freezing a legal action is the same class
   * of bug as letting an impossible one through.
   */
  canWriteGuests?: boolean;
}

export default function LogisticsTab(props: LogisticsTabProps) {
  const { t } = useTranslation();
  const {
    guests, newGuest, setNewGuest, addGuest, updateGuest, removeGuest, realId,
    members, flightNotes, onFlightNotesChange,
    accommodations, accSearch, searchAccommodation, accResults, setAccSearch, setAccResults, addAccommodation,
    addAccommodationFromResult, updateAccommodation, deleteAccommodation,
    roomingFor, upsertRooming, clearRooming,
    bandId, canWrite = true, canWriteGuests = canWrite,
  } = props;

  // LOT E1 — cards that HAVE content open themselves. Guestlist / accommodation
  // read what is already in props; transport and notes are loaded by the child
  // sections themselves, which report their state up (no extra fetch).
  const [transportHasData, setTransportHasData] = useState(false);
  const [miscNotesHasData, setMiscNotesHasData] = useState(false);
  const [notesHasData, setNotesHasData] = useState(false);

  const TAB_CARDS: StackCard[] = [
    // LOT E1 (2026-11-02): each card declares whether it HAS content, so it
    // opens itself on a fresh (no saved prefs) view instead of showing a full
    // card folded. `defaultOpen` is gone: none of these cards is worth
    // unfolding empty (rule: a folded card beats an empty unfolded one).
    { id: 'guestlist', hasData: guests.length > 0, title: t('logisticsTab.guestlist'), node: (<div data-logistics="guestlist">
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
    <input className="form-input" value={newGuest}
      placeholder={t('logisticsTab.addGuestPh')}
      style={{ flex: '1 1 220px', minWidth: 180 }}
      disabled={!canWriteGuests}
      onChange={(e) => setNewGuest(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') addGuest(); }} />
    <button className="btn btn-primary" onClick={addGuest} disabled={!newGuest.trim() || !canWriteGuests}>{t('logisticsTab.add')}</button>
  </div>
  {guests.length === 0 ? (
    <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('logisticsTab.noGuests')}</p>
  ) : (
    <div className="rc-list" data-guest-list>
      {guests.map((g) => (
        <div key={g.id} className="rc-card col" data-guest-row>
          <div className="rc-card-fields">
            <div className="form-field" style={{ flex: '2 1 180px', minWidth: 0 }}>
              <label>{t('logisticsTab.colName')}</label>
              <input className="form-input" value={g.name} disabled={!canWriteGuests} onChange={(e) => updateGuest(g, { name: e.target.value })} />
            </div>
            <div className="form-field" style={{ flex: '2 1 160px', minWidth: 0 }}>
              <label>{t('logisticsTab.colRole')}</label>
              <input className="form-input" value={g.role || ''} placeholder={t('logisticsTab.rolePh')} disabled={!canWriteGuests} onChange={(e) => updateGuest(g, { role: e.target.value })} />
            </div>
          </div>
          <div className="rc-card-fields" style={{ alignItems: 'flex-end' }}>
            <div className="form-field" style={{ flex: '1 1 150px', minWidth: 0 }}>
              <label>{t('logisticsTab.colStatus')}</label>
              <select className="form-input" value={g.check_in_status || 'pending'} disabled={!canWriteGuests}
                onChange={(e) => updateGuest(g, { check_in_status: e.target.value })}>
                <option value="pending">{t('logisticsTab.pending')}</option>
                <option value="checked_in">{t('logisticsTab.checkedIn')}</option>
                <option value="cancelled">{t('logisticsTab.cancelled')}</option>
              </select>
            </div>
            <div className="form-field" style={{ flex: '1 1 130px', minWidth: 0 }}>
              <label>{t('logisticsTab.colArrival')}</label>
              <input className="form-input" type="time" value={g.arrival_time || ''} placeholder={t('logisticsTab.hhmm')} disabled={!canWriteGuests} onChange={(e) => updateGuest(g, { arrival_time: e.target.value })} />
            </div>
            <button className="btn btn-secondary" style={{ flex: '0 0 auto', marginLeft: 'auto' }} onClick={() => removeGuest(g)} disabled={!canWriteGuests}>×</button>
          </div>
        </div>
      ))}
    </div>
  )}
</div>) },
    { id: 'transport', hasData: transportHasData || flightNotes.trim().length > 0, title: t('logisticsTab.transportTitle'), node: (<div data-logistics="transport">
  <TransportSection
    realId={realId}
    members={members}
    notes={flightNotes}
    onNotesChange={onFlightNotesChange}
    bandId={bandId}
    onHasDataChange={setTransportHasData}
  />
</div>) },
    { id: 'accommodation', hasData: accommodations.length > 0 || members.length > 0, title: `🏨 ${t('logisticsTab.accommodation')}`, sub: t('logisticsTab.accommodationHint'), node: (<div data-logistics="accommodation">
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 10px' }}>
      <input
        className="form-input"
        style={{ flex: '1 1 320px', maxWidth: 420, minWidth: 180 }}
        placeholder={t('logisticsTab.searchHotelPh')}
        value={accSearch}
        disabled={!canWrite}
        onChange={(e) => searchAccommodation(e.target.value)}
      />
      <span className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>{t('logisticsTab.or')}</span>
      <button className="btn btn-small btn-secondary" onClick={addAccommodation} disabled={!canWrite}>{t('logisticsTab.addHotelManual')}</button>
  </div>

  {accResults.length > 0 && (
    <div className="rc-list" data-acc-results style={{ maxWidth: 420, marginBottom: 10 }}>
      {accResults.map((r, i) => (
        <button
          key={i}
          type="button"
          className="btn btn-small"
          style={{ textAlign: 'left', justifyContent: 'flex-start' }}
          onClick={() => { void addAccommodationFromResult(r); setAccSearch(''); setAccResults([]); }}
          disabled={!canWrite}
        >
          <span style={{ fontWeight: 600 }}>{r.name || ''}</span>
          <span style={{ color: 'var(--text-dim)', marginLeft: 6 }}>
            {[r.address, r.city, r.country].filter(Boolean).join(', ')}
          </span>
        </button>
      ))}
    </div>
  )}

  <div className="rc-list" data-acc-list>
    {accommodations.map((a) => (
      <AccommodationRow key={a.id} a={a} onUpdate={(patch) => updateAccommodation(a, patch)} onDelete={() => deleteAccommodation(a.id)} canWrite={canWrite} />
    ))}
    {accommodations.length === 0 && (
      <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('logisticsTab.noHotels')}</p>
    )}
  </div>

  <h4 className="rc-section-title" style={{ margin: '20px 0 10px' }}>{t('logisticsTab.roomingList')}</h4>
  {members.length === 0 ? (
    <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('logisticsTab.noMembers')}</p>
  ) : (
    <div className="rc-list" data-rooming-list>
      {members.map((m) => {
        const r = roomingFor(m.id);
        const defaultHotel = accommodations[0]?.id ?? null;
        const hotelValue = r?.accommodation_id != null ? String(r.accommodation_id) : (defaultHotel != null ? String(defaultHotel) : '');
        return (
          <div key={m.id} className="rc-card col" data-rooming-row>
            <div className="rc-card-fields">
              <span style={{ flex: '1 1 170px', minWidth: 0, fontWeight: 600 }}>{m.name} <span style={{ color: 'var(--text-dim)' }}>({m.kind})</span></span>
              <input className="form-input" style={{ flex: '0 1 90px', minWidth: 64 }} placeholder={t('logisticsTab.roomNo')}
                value={r?.room_number || ''}
                disabled={!canWrite}
                onChange={(e) => upsertRooming(m.id, { room_number: e.target.value, accommodation_id: hotelValue ? Number(hotelValue) : null })} />
              <input className="form-input" style={{ flex: '1 1 110px', minWidth: 80 }} placeholder={t('logisticsTab.comment')}
                value={r?.room_comment || ''}
                disabled={!canWrite}
                onChange={(e) => upsertRooming(m.id, { room_comment: e.target.value, accommodation_id: hotelValue ? Number(hotelValue) : null })} />
              <select className="form-input" style={{ flex: '2 1 150px', minWidth: 130 }}
                value={hotelValue}
                disabled={!canWrite}
                onChange={(e) => upsertRooming(m.id, { accommodation_id: e.target.value ? Number(e.target.value) : null })}>
                {accommodations.length === 0 && <option value="">{t('logisticsTab.noHotel')}</option>}
                {accommodations.map((a) => (
                  <option key={a.id} value={a.id}>{a.hotel_name}</option>
                ))}
              </select>
              {r && (
                <button className="btn btn-small btn-danger" style={{ flex: '0 0 auto', marginLeft: 'auto' }} onClick={() => clearRooming(m.id)} disabled={!canWrite}>✕</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  )}
</div>) },
    { id: 'notes', hasData: miscNotesHasData || notesHasData, title: t('logisticsTab.notes'), node: (<div data-logistics="notes">
  <ShowNotesSection realId={realId} bandId={bandId} kind="misc" onHasDataChange={setMiscNotesHasData} />
  <div style={{ marginTop: 20 }}>
    <ShowNotesSection realId={realId} bandId={bandId} kind="notes" onHasDataChange={setNotesHasData} />
  </div>
</div>) },
  ];

  return (
    <div data-logistics-tab>
      {/* Guestlist */}
      <CardStack pageId="show_logistics" cards={TAB_CARDS} foldable />

      {/* Flights / transport — TransportSection is P4a-harmonised: only the
          chrome AROUND it belongs to this lot (marker + card wrapper). */}

      {/* Accommodation + Rooming list */}

      {/* Show notes — LOGISTICS (misc) + categorized NOTES, each a separate
          daysheet section. Band-scoped write gate handled inside ShowNotesSection. */}
    </div>
  );
}
